import React, { useState, useEffect, useCallback } from 'react';
import Calendar from 'react-calendar';
import { DollarSign, RefreshCw } from 'lucide-react';
import supabase from '../../supabase';
import './DashboardInvoiceCalendar.css';

export default function InvoiceReminderCalendar({ user, onSwitchToTracker }) {
  const [invoices, setInvoices] = useState([]);
  const [recurringInvoices, setRecurringInvoices] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [eventFilter, setEventFilter] = useState('all');

  const fetchInvoices = useCallback(async () => {
    try {
      const { data: invoiceData, error } = await supabase
        .from('invoices')
        .select(`
          id, invoice_number, due_date, status, total_amount, email_sent,
          is_recurring, recurring_interval, recurring_end,
          last_created_date,
          clients!fk_invoices_client (client_name)
        `);

      if (error) {
        console.error('❌ Error fetching invoices:', error);
        return;
      }

      const unpaidInvoices = invoiceData.filter(inv => {
        const isPaid = inv.status === 'Paid' || inv.status === 'paid';
        return !isPaid;
      });

      const transformedInvoices = unpaidInvoices.map(inv => ({
        ...inv,
        client_name: inv.clients?.client_name || 'Unknown Client'
      }));

      const recurringOnly = transformedInvoices
        .filter(inv => inv.is_recurring)
        .map(inv => ({
          id: inv.id,
          invoice_number: inv.invoice_number,
          client_name: inv.client_name,
          amount: inv.total_amount,
          email_sent: inv.email_sent,
          next_due: calculateNextRecurringDate(inv.last_created_date || inv.due_date, inv.recurring_interval),
          recurring_end: inv.recurring_end
        }))
        .filter(r => {
          if (!r.recurring_end) return true;
          return new Date(r.next_due) <= new Date(r.recurring_end);
        });

      const regularInvoices = transformedInvoices.filter(inv => !inv.is_recurring);

      setInvoices(regularInvoices);
      setRecurringInvoices(recurringOnly);
    } catch (error) {
      console.error('❌ Error in fetchInvoices:', error);
    }
  }, []);

  const calculateNextRecurringDate = (lastDate, interval) => {
    let date = new Date(lastDate);
    const today = new Date();

    while (date <= today) {
      switch (interval) {
        case 'daily': date.setDate(date.getDate() + 1); break;
        case 'weekly': date.setDate(date.getDate() + 7); break;
        case 'monthly': date.setMonth(date.getMonth() + 1); break;
        case 'quarterly': date.setMonth(date.getMonth() + 3); break;
        case 'yearly': date.setFullYear(date.getFullYear() + 1); break;
        default: date.setMonth(date.getMonth() + 1);
      }
    }
    return date.toISOString().split('T')[0];
  };

  useEffect(() => {
    fetchInvoices();

    const subscription = supabase
      .channel('invoices-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'invoices',
        },
        (payload) => {
          setTimeout(() => {
            fetchInvoices();
          }, 200);
        }
      )
      .subscribe();

    const pollInterval = setInterval(() => {
      fetchInvoices();
    }, 30000);

    return () => {
      supabase.removeChannel(subscription);
      clearInterval(pollInterval);
    };
  }, [fetchInvoices]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        fetchInvoices();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [fetchInvoices]);

  const calendarEvents = useCallback(() => {
    const today = new Date();
    const events = [];

    invoices.forEach(inv => {
      const dueDate = new Date(inv.due_date);
      const overdue = dueDate < today && inv.status !== 'Paid' && inv.status !== 'paid';

      events.push({
        date: inv.due_date,
        type: 'due',
        priority: overdue ? 'high' : 'medium',
        title: overdue ? `Overdue: ${inv.invoice_number}` : `Due: ${inv.invoice_number}`,
        client: inv.client_name,
        amount: inv.total_amount,
        invoice: inv,
        icon: DollarSign,
        status: overdue ? 'overdue' : 'unpaid',
        email_sent: inv.email_sent
      });
    });

    recurringInvoices.forEach(rec => {
      events.push({
        date: rec.next_due,
        type: 'recurring',
        priority: 'medium',
        title: `Recurring: ${rec.invoice_number}`,
        client: rec.client_name,
        amount: rec.amount,
        recurring: rec,
        icon: RefreshCw,
        email_sent: rec.email_sent
      });
    });

    return events;
  }, [invoices, recurringInvoices]);

  const events = calendarEvents();

  const eventsForDate = events.filter(e => {
    const dateStr = selectedDate.toISOString().split('T')[0];
    if (e.date !== dateStr) return false;

    if (eventFilter === 'all') return true;
    if (eventFilter === 'due') return e.type === 'due';
    if (eventFilter === 'recurring') return e.type === 'recurring';
    return true;
  });

  const tileContent = ({ date }) => {
    const dateStr = date.toISOString().split('T')[0];
    const dayEvents = events.filter(e => e.date === dateStr);
    if (dayEvents.length === 0) return null;

    const hasHigh = dayEvents.some(e => e.priority === 'high');
    const hasMedium = dayEvents.some(e => e.priority === 'medium');

    return (
      <div className="dashboardinvoicecalendar-calendar-indicators">
        <span className={`dashboardinvoicecalendar-indicator ${hasHigh ? 'high' : hasMedium ? 'medium' : 'low'}`} />
        {dayEvents.length > 1 && <span className="dashboardinvoicecalendar-count">{dayEvents.length}</span>}
      </div>
    );
  };

  const sendInvoice = (invoice) => {
    console.log(`Sending invoice ${invoice.invoice_number}`);
    if (onSwitchToTracker) {
      onSwitchToTracker();
    } else {
      alert(`Invoice sent for ${invoice.invoice_number}`);
    }
  };

  const sendReminder = (invoice) => {
    console.log(`Sending reminder for invoice ${invoice.invoice_number}`);
    if (onSwitchToTracker) {
      onSwitchToTracker();
    } else {
      alert(`Reminder sent for ${invoice.invoice_number}`);
    }
  };

  const sendRecurring = (recurring) => {
    console.log(`Sending recurring invoice ${recurring.invoice_number}`);
    if (onSwitchToTracker) {
      onSwitchToTracker();
    } else {
      alert(`Invoice sent for recurring ${recurring.invoice_number}`);
    }
  };

  return (
    <div className="dashboardinvoicecalendar-invoice-reminder-calendar">
      <div className="dashboardinvoicecalendar-calendar-header">
        <h2>Calendar</h2>
        <div className="dashboardinvoicecalendar-header-right">
          <div className="dashboardinvoicecalendar-legend">
            <div className="dashboardinvoicecalendar-legend-item">
              <span className="dashboardinvoicecalendar-indicator high"></span>
              High Priority
            </div>
            <div className="dashboardinvoicecalendar-legend-item">
              <span className="dashboardinvoicecalendar-indicator medium"></span>
              Medium Priority
            </div>
          </div>
          <select 
            value={eventFilter} 
            onChange={e => setEventFilter(e.target.value)} 
            className="dashboardinvoicecalendar-filter-select"
          >
            <option value="all">All Events</option>
            <option value="due">Due Dates</option>
            <option value="recurring">Recurring</option>
          </select>
        </div>
      </div>

      <div className="dashboardinvoicecalendar-calendar-container">
        <div className="dashboardinvoicecalendar-calendar-section">
          <Calendar
            onChange={setSelectedDate}
            value={selectedDate}
            tileContent={tileContent}
            className="dashboardinvoicecalendar-reminder-calendar"
          />
        </div>

        <div className="dashboardinvoicecalendar-events-section">
          <h3>
            {eventFilter === 'all' && 'Events'}
            {eventFilter === 'due' && 'Due Dates'}
            {eventFilter === 'recurring' && 'Recurring'}
            {' '}on {selectedDate.toDateString()}
          </h3>
          {eventsForDate.length === 0 ? (
            <p className="dashboardinvoicecalendar-no-events">No events for this date.</p>
          ) : (
            <div className="dashboardinvoicecalendar-events-list">
              {eventsForDate.map((e, idx) => (
                <div key={`${e.type}-${e.invoice?.id || e.recurring?.id}-${idx}`} className={`dashboardinvoicecalendar-event-item priority-${e.priority}`}>
                  <div className="dashboardinvoicecalendar-event-icon"><e.icon size={16} /></div>
                  <div className="dashboardinvoicecalendar-event-details">
                    <div className="dashboardinvoicecalendar-event-title">{e.title}</div>
                    <div className="dashboardinvoicecalendar-event-client">{e.client}</div>
                    <div className="dashboardinvoicecalendar-event-amount">₹{e.amount}</div>
                    {e.status && (
                      <div className="dashboardinvoicecalendar-event-status">
                        <span className={`dashboardinvoicecalendar-status-badge ${e.status}`}>{e.status.toUpperCase()}</span>
                      </div>
                    )}
                  </div>
                  <div className="dashboardinvoicecalendar-event-actions">
                    {e.type === 'due' && (
                      <>
                        {!e.email_sent ? (
                          <button className="dashboardinvoicecalendar-action-btn" onClick={() => sendInvoice(e.invoice)}>
                            Send Invoice
                          </button>
                        ) : (
                          <button className="dashboardinvoicecalendar-action-btn" onClick={() => sendReminder(e.invoice)}>
                            Send Reminder
                          </button>
                        )}
                      </>
                    )}
                    {e.type === 'recurring' && (
                      <>
                        {!e.email_sent ? (
                          <button className="dashboardinvoicecalendar-action-btn" onClick={() => sendRecurring(e.recurring)}>
                            Send Invoice
                          </button>
                        ) : (
                          <button className="dashboardinvoicecalendar-action-btn" onClick={() => sendReminder(e.recurring)}>
                            Send Reminder
                          </button>
                        )}
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}